import React from 'react';
import { Link } from 'react-router-dom';
import Section from '../components/Section';
import Card from '../components/Card';
import Button from '../components/Button';
import Profile from '../components/Profile';
import FAQAccordion from '../components/FAQAccordion';
import SEO from '../components/SEO';
import content from '../data/content.json';

const ArvOgTestament: React.FC = () => {
  const arvData = content.arvOgTestament;
  const heroSection = arvData.content.find(sec => sec.section === 'Hero');
  const pricingSection = arvData.content.find(sec => sec.section === 'Pricing');
  const textImageSection = arvData.content.find(sec => sec.section === 'TextImage');
  const servicesSection = arvData.content.find(sec => sec.section === 'Services');
  const whenYouNeedSection = arvData.content.find(sec => sec.section === 'WhenYouNeed');
  const useCasesSection = arvData.content.find(sec => sec.section === 'UseCases');
  const lawyersSection = arvData.content.find(sec => sec.section === 'Lawyers');
  const localExpertiseSection = arvData.content.find(sec => sec.section === 'LocalExpertise');
  const faqSection = arvData.content.find(sec => sec.section === 'FAQ');
  const ctaFooterSection = arvData.content.find(sec => sec.section === 'CTAFooter');

  // Image mapping for user-provided images
  const imageMap: { [key: string]: string } = {
    'hero-arv': '/privateImages/argOgTestament/ThorleifOgToveBildeSammen.webp',
    'sikre-arvinger': '/privateImages/argOgTestament/sikredegselvogdinearvinger.webp',
    'when-need': '/privateImages/argOgTestament/toveogthorleifnaartrengerduettestamente.webp',
  };

  // Map lawyer names to photos (using existing photos from About page)
  const photoMap: { [key: string]: string } = {
    'Tove Kristin Farstad': '/privateImages/tove.webp',
    'Thorleif G. Bruland': '/privateImages/throleif.webp',
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": ["Service", "LegalService"],
    "name": "Arv og Testament - Advokatfirmaet Bruland Oslo",
    "provider": {
      "@type": "LegalService",
      "name": "Advokatfirmaet Bruland",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Drammensveien 82c",
        "addressLocality": "Oslo",
        "postalCode": "0271",
        "addressCountry": "NO"
      },
      "geo": {
        "@type": "GeoCoordinates",
        "latitude": "59.9136930",
        "longitude": "10.7021640"
      },
      "areaServed": {
        "@type": "City",
        "name": "Oslo"
      }
    },
    "serviceType": "Arv og Testament Advokat Oslo",
    "areaServed": {
      "@type": "City",
      "name": "Oslo"
    },
    "description": "Profesjonell juridisk bistand ved opprettelse av testament, arveoppgjør og gaver i live i Oslo. Vi hjelper deg med å sikre dine arvinger og planlegge fremtiden."
  };

  // FAQ structured data so Google understands the questions this page answers
  // (e.g. "hva koster det å skrive testamente"). Built from the same FAQ content.
  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": (faqSection?.faqItems || []).map((f: any) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.answer
      }
    }))
  };

  return (
    <>
      <SEO
        title="Testament & Arv – Advokat i Oslo | Advokatfirmaet Bruland"
        description="Advokat i Oslo for testament og arv. Vi hjelper deg med å opprette testament, arveoppgjør og skifte – se hva det koster. Ring 22 53 53 22 for veiledning."
        keywords="advokat arv oslo, testament advokat oslo, arveoppgjør oslo, skifte advokat oslo, arv og testament oslo, testament oslo, arv uskifte, gaver i live, fremtidsfullmakt oslo, privat skifte oslo"
        canonicalUrl="/arv-og-testament"
        structuredData={[structuredData, faqStructuredData]}
        ogImage="https://bruland.no/privateImages/argOgTestament/ThorleifOgToveBildeSammen.webp"
      />
      {/* Hero Section - 2 columns: image left, text+bullets right */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div className="order-2 lg:order-1">
              {heroSection?.image && imageMap[heroSection.image] ? (
                <img
                  src={imageMap[heroSection.image]}
                  alt="Advokater Thorleif Bruland og Tove Farstad - spesialister innen arv og testament i Oslo"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-lg flex items-center justify-center">
                  <svg className="w-24 h-24 text-white/30" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>

            {/* Right: Text + Bullets + CTA */}
            <div className="order-1 lg:order-2">
              <h1 className="text-4xl md:text-5xl font-bold mb-6 font-serif text-gray-900">
                {heroSection?.title}
              </h1>
              <ul className="space-y-4 mb-8">
                {heroSection?.bulletPoints?.map((point, idx) => (
                  <li key={idx} className="flex items-start">
                    <svg className="w-6 h-6 text-accent mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-lg text-gray-700">{point}</span>
                  </li>
                ))}
              </ul>
              <a href={`tel:${heroSection?.phone?.replace(/\s/g, '')}`}>
                <Button size="lg">{heroSection?.ctaText}</Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <Section title={pricingSection?.title} background="gray" spacing="lg" centered container="narrow">
        <p className="text-lg text-gray-600 mb-8 leading-relaxed">
          {pricingSection?.description}
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mt-8">
          <a href={`tel:${pricingSection?.phone?.replace(/\s/g, '')}`}>
            <Button size="lg">Ring {pricingSection?.phone}</Button>
          </a>
          <Link to="/kontakt">
            <Button variant="outline" size="lg">Send oss mail</Button>
          </Link>
        </div>
      </Section>

      {/* Cost Section - visibly answers a top search query: "hva koster det å skrive testament hos advokat" */}
      <Section
        title="Hva koster det å skrive testament hos advokat?"
        background="white"
        spacing="lg"
        container="narrow"
        centered
      >
        <p className="text-lg text-gray-600 leading-relaxed mb-8">
          Det finnes ingen fast pris på et testament – kostnaden avhenger av flere
          forhold, og av hvilken bistand du trenger. Et enkelt testament er rimeligere
          enn et som krever grundigere juridiske vurderinger. Vi anbefaler å starte med
          veiledningstimen beskrevet ovenfor, slik at du får oversikt over hva nettopp
          din sak krever og et pristilbud tilpasset deg.
        </p>
        <p className="text-lg text-gray-700 font-semibold mb-4">
          Hva et testament koster avhenger blant annet av:
        </p>
        <ul className="space-y-3 text-left max-w-2xl mx-auto mb-10">
          {[
            'Hvor omfattende og komplekst testamentet er',
            'Om det er et enkelt testament eller et gjensidig testament for ektefeller/samboere',
            'Om du også trenger relaterte dokumenter, som fremtidsfullmakt',
            'Din konkrete situasjon og behov for individuell rådgivning',
          ].map((item, idx) => (
            <li key={idx} className="flex items-start">
              <svg className="w-6 h-6 text-accent mr-3 flex-shrink-0 mt-1" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              <span className="text-lg text-gray-700">{item}</span>
            </li>
          ))}
        </ul>
        <div className="flex justify-center">
          <Link to="/kontakt">
            <Button size="lg">Be om pristilbud</Button>
          </Link>
        </div>
      </Section>

      {/* Text + Image Section - text left, image right */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Text */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif text-gray-900">
                {textImageSection?.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {textImageSection?.content}
              </p>
            </div>

            {/* Right: Image */}
            <div>
              {textImageSection?.image && imageMap[textImageSection.image] ? (
                <img
                  src={imageMap[textImageSection.image]}
                  alt="Testament advokat Oslo - bistår med rådgivning om testament og arvesaker for å sikre dine arvinger"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-lg flex items-center justify-center">
                  <svg className="w-24 h-24 text-white/30" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Services Section - 3 column grid */}
      <Section title={servicesSection?.title} background="gray" spacing="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
          {servicesSection?.services?.map((service: any) => (
            <Card key={service.id} variant="feature">
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* When You Need Section - image left, text right (reversed) */}
      <section className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left: Image */}
            <div>
              {whenYouNeedSection?.image && imageMap[whenYouNeedSection.image] ? (
                <img
                  src={imageMap[whenYouNeedSection.image]}
                  alt="Advokater i Oslo - Tove og Thorleif gir råd om når du trenger et testamente"
                  className="w-full h-auto rounded-lg shadow-lg"
                />
              ) : (
                <div className="w-full h-96 bg-gradient-to-br from-primary-light to-primary rounded-lg flex items-center justify-center">
                  <svg className="w-24 h-24 text-white/30" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                  </svg>
                </div>
              )}
            </div>

            {/* Right: Text */}
            <div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 font-serif text-gray-900">
                {whenYouNeedSection?.title}
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                {whenYouNeedSection?.content}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases Section - 3 column responsive grid */}
      <Section title={useCasesSection?.title} background="gray" spacing="lg">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
          {useCasesSection?.cases?.map((useCase: any) => (
            <Card key={useCase.id} hoverable>
              <h3 className="text-xl font-bold text-gray-900 mb-3 font-serif">
                {useCase.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {useCase.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Lawyers Section - Simple layout without cards */}
      <Section title={lawyersSection?.title} background="white" spacing="lg" centered>
        <p className="text-lg text-gray-600 mb-12 text-center max-w-3xl mx-auto">
          {lawyersSection?.description}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 max-w-4xl mx-auto">
          {lawyersSection?.profiles?.map((lawyer: any, idx: number) => (
            <div key={idx} className="flex flex-col items-center text-center">
              {/* Photo */}
              {photoMap[lawyer.name] ? (
                <img
                  src={photoMap[lawyer.name]}
                  alt={`Portrett av advokat ${lawyer.name}`}
                  className="w-48 h-48 rounded-full mb-6 object-cover border-4 border-gray-200"
                />
              ) : (
                <div className="w-48 h-48 rounded-full mb-6 bg-gradient-to-br from-gray-200 to-gray-300 flex items-center justify-center">
                  <svg className="w-24 h-24 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                  </svg>
                </div>
              )}

              {/* Name */}
              <h3 className="text-2xl font-bold text-gray-900 mb-2 font-serif">{lawyer.name}</h3>

              {/* Title */}
              <p className="text-lg text-accent mb-4">{lawyer.title}</p>

              {/* Description */}
              <p className="text-gray-600 leading-relaxed">
                {lawyer.description}
              </p>
            </div>
          ))}
        </div>
      </Section>

      {/* Local Expertise Section */}
      <Section title={localExpertiseSection?.title} background="white" spacing="lg" container="narrow" centered>
        <p className="text-lg text-gray-600 leading-relaxed mb-6">
          {localExpertiseSection?.content}
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center items-stretch mt-8">
          <a href={`tel:${localExpertiseSection?.phone?.replace(/\s/g, '')}`} className="w-full sm:w-auto flex">
            <Button size="lg" className="w-full sm:w-auto min-w-[200px] flex-1">Ring {localExpertiseSection?.phone}</Button>
          </a>
          <Link to="/kontakt" className="w-full sm:w-auto flex">
            <Button variant="outline" size="lg" className="w-full sm:w-auto min-w-[200px] flex-1">Besøk oss i Oslo</Button>
          </Link>
        </div>
      </Section>

      {/* FAQ Section - Accordion */}
      <Section title={faqSection?.title} background="gray" spacing="lg" container="narrow">
        <div className="mt-8">
          <FAQAccordion items={faqSection?.faqItems || []} />
        </div>
      </Section>

      {/* CTA Footer Section */}
      <section className="bg-primary text-white text-center py-20">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-4xl font-bold mb-4 font-serif text-white">
            {ctaFooterSection?.title}
          </h2>
          <p className="text-xl mb-8 text-white/90">
            {ctaFooterSection?.subtitle}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${ctaFooterSection?.phone?.replace(/\s/g, '')}`}>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Ring {ctaFooterSection?.phone}
              </Button>
            </a>
            <a href={`mailto:${ctaFooterSection?.email}`}>
              <Button variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-primary">
                Send oss mail
              </Button>
            </a>
          </div>
        </div>
      </section>
    </>
  );
};

export default ArvOgTestament;
